import type { HistorySequence } from "@live-documentation/engine/live-docs/history/format";

import { inferDefaultEntryNodeId } from "./bootstrap";
import { landingOf } from "./bootstrap/landing";
import { createDetailPanel, type DetailPanelAction } from "./detailPanel";
import { requireElement, setActiveView } from "./dom";
import { downloadDocs, downloadFactsJson, type DownloadBundleType, type DownloadFormat } from "./download";
import { attachGlobalErrorHandler, reportFatalExplorerError } from "./errors";
import { buildTestCoverageMap, resolveLinkEndpoint, getInputById } from "./graph-helpers";
import { historicalDirectories, historicalNode } from "./history/file-identity";
import { createHistoryControl, isHistorySequence } from "./history/history-control";
import type { HistoryMoment } from "./history/moment";
import { endFiles, momentGround, todayGround, type PathGround } from "./history/moment-paths";
import { applyLayoutSettings, layoutSettingsOf } from "./layout-presets";
import { iconButton } from "./panels/icon-button";
import { createItemMenu } from "./panels/item-menu";
import { createPinTray } from "./panels/pin-tray";
import { sourcesFacts } from "./panels/sources-facts";
import { renderSourcesView } from "./panels/sources-view";
import { statsLineOf } from "./panels/stats-line";
import { initTuningPanel } from "./panels/tuning";
import { findPath, reachOf, referencesAgainstPath, type PathfindResult, type Reach } from "./pathfind";
import {
  parseInitialState,
  getDefaultFilters,
  getDefaultTuning,
  readPersistedUi,
  applyPersistedUi,
  readPersistedNav,
  createPersistUiScheduler,
  createPersistNavScheduler
} from "./persistence";
import { compressSnapshot, readUrlState, scrubSnapshot, writeUrlState, type UrlStateSnapshot } from "./persistence/compressed-url-state";
import { canGoBack, canGoForward, onHistoryChange, startHistory } from "./persistence/history";
import { placeOf } from "./persistence/place";
import { searchIndexOf, type SearchHit } from "./search/graph-search";
import { createSearchBar, type SearchWords } from "./search/search-bar";
import type { ExplorerState, InspectionTarget, LocalMapPerspective, PathDestination, SpatialLayout, ViewName } from "./types";
import { defaultSpatialLayout } from "./views/forceGraphScope";
import { createForceGraphView } from "./views/forceGraphView";
import { createLocalView } from "./views/localView";
import { explorerGraphOf } from "../shared/graph";
import type { StaticExplorerData } from "../shared/staticExplorerData";
import type { ExplorerNodePayload } from "../shared/types";
import { closeDirectory as closeOpenedDirectory, openDirectory as addOpenedDirectory, under, type OpenDirectories } from "./views/localView/directory-state";
import { animatePerspective, captureLocalScene, holdPerspective } from "./views/perspectiveTransition";
import { addPin, EMPTY_PIN_SET, INTERNAL_SYMBOL, isSymbolPinned, pressSymbol, removePinsForNode, retainFile, type PinSet } from "./views/pin-state";
import { clearRetainedDirectory, describeRetained, newestRetained, reconcileRetained, releaseRetained, retainedKey, subjectAfterRelease, type RetainedItem, type RetainedState } from "./views/retained-state";
import { createWorldMapView } from "./views/worldMap";

attachGlobalErrorHandler();

void bootstrapExplorer();

/**
 * Bootstrap the explorer from the bundle the static builder wrote.
 */
async function bootstrapExplorer(): Promise<void> {
  try {
    const bundle = await loadExplorerData();
    // An address that names a commit of the history opens on it: the sequence is fetched before the first picture is
    // drawn, so that the page draws that moment once rather than today and then the moment.
    let snapshot = readUrlState();
    const navigation = parseInitialState();
    if (navigation.hasUrlState) snapshot = { ...snapshot, openDirectories: navigation.openDirectories };
    else {
      const saved = readPersistedNav();
      snapshot = { ...snapshot, selectedNodeId: saved?.nodeId ?? null, inspection: saved?.inspection,
        discovery: saved?.discovery, openDirectories: new Set(saved?.openDirectories ?? []) };
    }
    const needsHistory = snapshot.commit || selectedFileIds(snapshot).some(id => id.startsWith("history:"));
    const history = needsHistory && bundle.history ? await loadHistory(bundle.history.file) : undefined;
    startExplorer(bundle, snapshot, history);
  } catch (error) {
    reportFatalExplorerError(error);
  }
}

/** File identities a shared place needs, including retained past-only files when the picture is back at today. */
function selectedFileIds(snapshot: UrlStateSnapshot): string[] {
  return [snapshot.selectedNodeId, ...snapshot.pinSet.entries.map(pin => pin.nodeId),
    snapshot.inspection?.kind === "file" ? snapshot.inspection.nodeId : null,
    snapshot.destination?.kind === "file" ? snapshot.destination.nodeId : null,
    snapshot.reachFrom?.kind === "file" ? snapshot.reachFrom.nodeId : null,
    snapshot.discovery?.kind === "file" ? snapshot.discovery.nodeId : null].filter((id): id is string => !!id);
}

/** Where the bundle's data is read from: the path named by `?data=`, or `explorer-data.json` beside the page. */
function dataUrl(): URL {
  return onOwnSite(new URLSearchParams(window.location.search).get("data") ?? "./explorer-data.json", window.location.href);
}

/**
 * A path resolved against a base, refused unless it lies on the page's own site: no address and no bundle can make the
 * page read from anywhere else. The page's Content-Security-Policy refuses the same, and this says why in words.
 */
function onOwnSite(path: string, base: string | URL): URL {
  const url = new URL(path, base);
  if (url.origin !== window.location.origin) {
    throw new Error(`The Explorer reads only from its own site, and ${url.origin} is not it.`);
  }
  return url;
}

/** Loads the history file the bundle names, which lies beside its data. */
async function loadHistory(file: string): Promise<HistorySequence> {
  const url = onOwnSite(file, dataUrl());
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`No history at ${url.pathname} (${response.status}).`);
  }
  const sequence: unknown = await response.json();
  if (!isHistorySequence(sequence)) {
    throw new Error(`${url.pathname} is not a history this Explorer reads.`);
  }
  return sequence;
}

/**
 * Loads the bundle: from the path named by `?data=` on the page's own site, or from `explorer-data.json` beside the page.
 */
async function loadExplorerData(): Promise<StaticExplorerData> {
  const url = dataUrl();
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      `No explorer data at ${url.pathname} (${response.status}). Build a static bundle first with \`npm run live-docs:visualize\`.`
    );
  }
  const bundle = (await response.json()) as Partial<StaticExplorerData>;
  if (!bundle.graph || typeof bundle.graph.files !== "object") {
    throw new Error(`${url.pathname} is not an Explorer bundle: it holds no graph.`);
  }
  return bundle as StaticExplorerData;
}

function startExplorer(bundle: StaticExplorerData, initialSnapshot: UrlStateSnapshot, preloadedHistory?: HistorySequence): void {
  const graphData = explorerGraphOf(bundle.graph);
  const files = bundle.graph.files;
  const { bundledMarkdown, bundledMarkdownTree, relatedDocLinks } = bundle;

  // Related markdown, as the Knowledge Sources view lists it
  const bundledDocs = bundledMarkdownTree && bundledMarkdown
    ? { tree: bundledMarkdownTree, count: Object.keys(bundledMarkdown).length }
    : undefined;

  // ─────────────────────────────────────────────────────────────────────────
  // URL + LocalStorage State
  // ─────────────────────────────────────────────────────────────────────────

  const initialState = parseInitialState();

  const defaults = { filters: getDefaultFilters(), tuning: getDefaultTuning() };
  const persistedUi = readPersistedUi();
  const initialUi = applyPersistedUi(defaults, persistedUi);

  const savedNav = readPersistedNav();
  const persistedNav = initialState.hasUrlState ? null : savedNav;

  const resolveInitialView = (): ViewName => {
    if (initialState.hasUrlState) {
      return initialState.view;
    }
    // A bundle that carries a board opens on the World Map, the outside of everything.
    return persistedNav?.view ?? (bundle.board ? "world" : initialState.view);
  };

  const state: ExplorerState = {
    view: resolveInitialView(),
    perspective: initialState.perspective ?? savedNav?.perspective ?? "2d",
    spatialLayout: initialState.spatialLayout ?? savedNav?.spatialLayout ?? defaultSpatialLayout(graphData.nodes),
    selectedNode: null,
    inspection: null,
    discovery: null,
    filters: initialUi.filters,
    tuning: applyLayoutSettings(initialUi.tuning, readUrlState().layout)
  };

  const persistUi = createPersistUiScheduler(() => ({
    filters: state.filters,
    tuning: state.tuning
  }));
  const schedulePersistUi = persistUi.schedule;

  const currentNodesById = new Map(graphData.nodes.map(node => [node.id, node]));
  // Historical selection identities are a lookup alongside today's files, never additional nodes in today's graph.
  const nodesById = new Map(currentNodesById);
  const historyDirectories = preloadedHistory ? historicalDirectories(preloadedHistory) : new Set<string>();
  if (preloadedHistory) for (const id of selectedFileIds(initialSnapshot)) {
    const node = historicalNode(preloadedHistory, id);
    if (node) nodesById.set(id, node);
  }
  // The pins and the opened directories ride the address together; `?dir=` names an opened directory on its own.
  const initialExploration = scrubSnapshot(initialSnapshot, nodesById, bundledMarkdown, directory => historyDirectories.has(directory));
  if (new URLSearchParams(window.location.search).has("s")) state.filters = { ...state.filters, ...initialExploration.filters };
  state.pins = initialExploration.pinSet;
  state.openDirectories = initialExploration.openDirectories;
  state.retentionOrder = initialExploration.retentionOrder;
  state.destination = initialExploration.destination;
  state.reachFrom = initialExploration.reachFrom;
  state.historyCommit = initialExploration.commit;
  state.showAuthors = initialExploration.authors;
  state.inspection = initialExploration.inspection ?? null;
  state.discovery = initialExploration.discovery ?? (state.inspection?.kind === "file" ? { kind: "file", nodeId: state.inspection.nodeId }
    : state.inspection?.kind === "directory" ? state.inspection : null);

  const persistNav = createPersistNavScheduler(() => ({
    view: state.view,
    perspective: state.perspective,
    spatialLayout: state.spatialLayout,
    openDirectories: state.openDirectories ?? new Set(),
    selectedNodeId: state.selectedNode?.id ?? null,
    inspection: state.inspection,
    discovery: state.discovery
  }));
  const schedulePersistNav = persistNav.schedule;
  window.addEventListener("pagehide", () => { persistUi.flush(); persistNav.flush(); });

  const detailPanel = createDetailPanel(nodesById, {
    files,
    bundledMarkdown,
    history: () => shownMoment,
    onClose: () => closeInspection(),
    onNodeClick: (nodeId, symbol) => openInspection({ kind: "file", nodeId, ...(symbol ? { symbol } : {}) }),
    onDirectoryClick: directory => openInspection({ kind: "directory", directory }),
    onBundledDocClick: (path, fragment) => openInspection({ kind: "bundled-doc", path, ...(fragment ? { fragment } : {}) })
  });
  const itemMenu = createItemMenu();
  const hasBundledDoc = (path: string): boolean => !!bundledMarkdown && Object.hasOwn(bundledMarkdown, path);
  /** The moment of the history the 3D picture shows, or null for today. */
  let shownMoment: HistoryMoment | null = null;
  // The ground a path, its reach and the report are asked over: today's files and wires, or the moment's while the
  // picture shows one, from the moment the history control built for the view (2026-10-30).
  const groundToday = todayGround(currentNodesById, graphData.links);
  let groundShown: { moment: HistoryMoment; ground: PathGround } | null = null;
  function pathGround(): PathGround {
    if (!shownMoment) return groundToday;
    if (groundShown?.moment !== shownMoment) groundShown = { moment: shownMoment, ground: momentGround(shownMoment) };
    return groundShown.ground;
  }
  /** The moment shown, in the words the reader and the path report use; null at today. */
  function momentWords(): string | null {
    return shownMoment ? `${shownMoment.commit.date.slice(0, 10)}, commit ${shownMoment.commit.id.slice(0, 8)}` : null;
  }
  /** The path report's first words at a moment, naming the commit it was asked at; null at today. */
  function reportAt(): string | null {
    const moment = momentWords();
    return moment ? `At ${moment}:` : null;
  }
  let inspectionOpener: HTMLElement | null = null;
  const readerRoom = window.matchMedia("(max-width: 760px), (max-height: 480px)");
  const narrowNavigation = window.matchMedia("(max-width: 760px)");
  const sidebar = requireElement("sidebar");
  if (narrowNavigation.matches) sidebar.classList.add("collapsed");
  narrowNavigation.addEventListener("change", event => {
    if (event.matches) sidebar.classList.add("collapsed");
    syncSidebarToggle();
  });
  syncSidebarToggle();

  // Use imported resolveLinkEndpoint and inferDefaultEntryNodeId from extracted modules

  const testCoverage = buildTestCoverageMap(graphData, resolveLinkEndpoint, nodesById);

  const filterToggleTests = getInputById("filter-toggle-tests");
  const filterToggleAssets = getInputById("filter-toggle-assets");
  const filterToggleRelatedDocs = getInputById("filter-toggle-related-docs");
  const filterToggleCitingDocs = getInputById("filter-toggle-citing-docs");
  const statsLine = requireElement("stats-line");
  const statsText = document.createTextNode("");
  statsLine.replaceChildren(statsText);
  /** The sidebar's counts follow the picture: today's, or the moment of the history the 3D picture shows (2026-10-30). */
  // How many files the 3D picture shows of those the filters allow (discovery, 2026-10-31), and the repository's groups
  // over its references with the files standing alone (2026-11-02), beside the counts. The door to the whole picture
  // was a text link here until 2026-11-02, which the owner never found; it is the Whole toggle of the toolbar now.
  let shownFiles: { shown: number; total: number; groups: number; alone: number } | null = null;
  let backwardConnections = 0;
  const syncStatsLine = (): void => {
    const counts = statsLineOf((shownMoment?.graph ?? graphData).stats);
    const n = (count: number): string => count.toLocaleString("en-US");
    const shown = state.view === "map" && state.perspective === "3d" && shownFiles
      ? ` · ${n(shownFiles.shown)} of ${n(shownFiles.total)} files shown · ${n(shownFiles.groups)} ${shownFiles.groups === 1 ? "group" : "groups"}, ${n(shownFiles.alone)} alone` : "";
    statsText.data = counts + shown;
  };
  syncStatsLine();

  const localView = createLocalView({
    state,
    // The open reader covers the right of the map; a fit keeps the picture out from under it (2026-10-30). Its box is
    // its layout box, where it stands once its slide-in has ended, not where the transition has it at this instant.
    obstruction: () => {
      const panel = requireElement("detail-panel");
      if (!panel.classList.contains("visible")) return null;
      const host = (panel.offsetParent ?? document.body).getBoundingClientRect();
      return new DOMRect(host.left + panel.offsetLeft, host.top + panel.offsetTop, panel.offsetWidth, panel.offsetHeight);
    },
    bottomObstructions: () => [...document.querySelectorAll<HTMLElement>(".perspective-controls:not([hidden]), #controls")]
      .map(element => element.getBoundingClientRect()).filter(rect => rect.width > 0 && rect.height > 0),
    onPressPin: pressPin,
    onUnpinNode: unpinNode,
    onCloseNode: closePinnedNode,
    onOpenDirectory: openRetainedDirectory,
    onCloseDirectory: closeRetainedDirectory,
    graphData,
    resolveLinkEndpoint,
    onSelectNode: node => handleNodeClick(node),
    onRecenterNode: node => recentreOn(node),
    onFocusSidebar: (node, symbol) => focusSidebar(node, symbol),
    onInspectDirectory: directory => openInspection({ kind: "directory", directory }),
    onShowNodeActions: showNodeActions,
    onShowDirectoryActions: showDirectoryActions,
    onBackwardConnections: count => {
      if (count === backwardConnections) return;
      backwardConnections = count;
      syncPerspectiveControls();
    },
    testCoverage,
    nodesById
  });

  const forceGraphView = createForceGraphView({
    getPath: () => localView.getActivePath()?.nodeIds,
    state,
    graphData,
    nodesById,
    resolveLinkEndpoint,
    relatedDocLinks,
    hasBundledDoc: hasBundledDoc,
    onShowBundledDoc: (docPath: string) => {
      showBundledDocInDetailPanel(docPath);
    },
    onToggleNode: toggleSpatialNode,
    onTogglePin: toggleSpatialPin,
    onRecenterNode: recentreOn,
    onShowNodeActions: showNodeActions,
    onShowDirectoryActions: showDirectoryActions,
    onToggleDirectory: toggleSpatialDirectory,
    onCentreDirectory: centreOnDirectory,
    onOpenDirectory: directory => {
      openRetainedDirectory(directory);
      forceGraphView.focusDirectory(directory);
    },
    onCloseDirectory: closeRetainedDirectory,
    onShown: (shown, total, groups, alone) => {
      if (shownFiles?.shown === shown && shownFiles.total === total && shownFiles.groups === groups && shownFiles.alone === alone) return;
      shownFiles = { shown, total, groups, alone };
      syncStatsLine();
    }
  });

  // The history of the 3D Local Map: the scrubber and the step panel over the sequence the bundle carries (ticket C4).
  const historyControl = createHistoryControl({
    parent: requireElement("view-graph"),
    room: requireElement("main"),
    bundled: bundle.history,
    preloaded: preloadedHistory,
    load: () => loadHistory(bundle.history!.file),
    nodesById,
    onMoment: (moment, renames, energy, changes) => {
      shownMoment = moment;
      // The path and the reach are asked again over the moment's wires, and the absences said, before the picture is drawn.
      const origin = newestRetained(retentionState());
      if (origin && state.destination) reportPath(origin, state.destination, false);
      syncRetainedWords();
      forceGraphView.setMoment(moment, renames, energy, changes);
      if (state.inspection) renderInspection();
      syncReaderNote();
      syncStatsLine();
    },
    onSettle: () => forceGraphView.settle(),
    onCommit: commit => {
      if ((state.historyCommit ?? null) === commit) { syncPerspectiveControls(); return; }
      state.historyCommit = commit;
      persistExploration();
    },
    onAuthors: shown => {
      state.showAuthors = shown;
      persistExploration();
    },
    onPinFile: path => {
      const node = nodesById.get(path);
      if (node) pinHit({ kind: "file", node });
    },
    hasDocument: hasBundledDoc,
    onReadDocument: path => openInspection({ kind: "bundled-doc", path }),
    onOpenChange: () => syncPerspectiveControls(),
    // The scrubber's speed is Tuning's Play Rate, chosen where play is (2026-10-31).
    onPlayRate: rate => {
      state.tuning.history.playRate = rate;
      schedulePersistUi();
      syncTuningControls();
      syncPerspectiveControls();
    }
  });

  // Opening a thing is a new Local Map scope, rendered in the remembered representation.
  const openThingInLocalMap = (thing: { name: string; folder: string }): void => {
    leavePath();
    state.discovery = null;
    state.pins = { entries: [] };
    state.openDirectories = new Set([thing.folder]);
    state.selectedNode = null;
    closeInspection(false);
    changePerspective(state.perspective, null);
    persistExploration();
  };

  const worldMapView = createWorldMapView({
    root: requireElement("world-root"),
    graph: bundle.graph,
    board: bundle.board,
    onOpenFile: (file: string) => {
      const node = nodesById.get(file);
      if (node) {
        openLocalViewForNode(node);
      }
    },
    onOpenThing: openThingInLocalMap
  });

  syncFilterControls();

  if (filterToggleTests) {
    filterToggleTests.addEventListener("change", event => {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }
      state.filters.showTests = event.target.checked;
      schedulePersistUi();
      persistExploration();
      renderCurrentView();
    });
  }

  if (filterToggleAssets) {
    filterToggleAssets.addEventListener("change", event => {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }
      state.filters.showAssets = event.target.checked;
      schedulePersistUi();
      persistExploration();
      renderCurrentView();
    });
  }

  if (filterToggleRelatedDocs) {
    filterToggleRelatedDocs.addEventListener("change", event => {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }
      state.filters.showRelatedDocs = event.target.checked;
      schedulePersistUi();
      persistExploration();
      // Only re-render Force Graph (Related Docs only affect that view)
      if (state.view === "map" && state.perspective === "3d") {
        forceGraphView.render();
      }
    });
  }

  if (filterToggleCitingDocs) {
    filterToggleCitingDocs.addEventListener("change", event => {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }
      state.filters.showCitingDocs = event.target.checked;
      schedulePersistUi();
      persistExploration();
      if (state.view === "map" && state.perspective === "3d") {
        forceGraphView.render();
      }
    });
  }

  const perspectiveControls = document.createElement("div");
  perspectiveControls.className = "perspective-controls";
  perspectiveControls.setAttribute("aria-label", "Local Map layout and representation");
  // Whole or Local (2026-11-02, in the slot of the Compact and Relaxed packing presets, which the owner found
  // "surprisingly unuseful"): the scan root opened, which in 3D is every file and in 2D the root's own level around the
  // retained branches, or released, which is what was asked about. The one state the address already carries.
  const wholeButton = iconButton("Whole", '<circle cx="12" cy="12" r="9"/><g fill="currentColor" stroke="none"><circle cx="9" cy="9.5" r="1.3"/><circle cx="14.5" cy="8" r="1.3"/><circle cx="15" cy="13.5" r="1.3"/><circle cx="9.5" cy="15" r="1.3"/><circle cx="12" cy="12" r="1.3"/></g>');
  const nothingAsked = (): boolean => !state.pins?.entries.length && !state.selectedNode && !state.openDirectories?.size && !state.discovery && !localView.getActivePath();
  wholeButton.addEventListener("click", () => {
    historyControl.pause();
    if (state.openDirectories?.has("")) closeRetainedDirectory("");
    else openRetainedDirectory("");
    if (state.perspective === "3d") forceGraphView.fit();
  });
  const tuningButton = iconButton("Layout tuning", '<path d="M4 6h16M4 12h16M4 18h16M8 3v6m8 0v6M10 15v6"/>');
  tuningButton.addEventListener("click", () => {
    document.getElementById("sidebar")?.classList.remove("collapsed");
    const tuning = document.querySelector<HTMLDetailsElement>(".sidebar-details");
    if (tuning) {
      tuning.open = true;
      tuning.querySelector<HTMLElement>("summary")?.focus();
      tuning.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  });
  const mapButton = document.createElement("button");
  mapButton.textContent = "2D";
  mapButton.title = "Local Map in 2D";
  mapButton.addEventListener("click", () => changePerspective("2d"));
  const graphButton = document.createElement("button");
  graphButton.textContent = "3D";
  graphButton.title = "Local Map in 3D";
  graphButton.addEventListener("click", () => changePerspective("3d"));
  // The 3D layout: the emergent network of the analyzed references, or the emergent tree of the declared directories
  // (the owner's distinction, 2026-10-14). A choice inside 3D, shown only there.
  const connectionsButton = iconButton("Connections", '<circle cx="12" cy="6" r="2.5"/><circle cx="6" cy="17" r="2.5"/><circle cx="18" cy="17" r="2.5"/><path d="m10.8 8.3-3.6 6.4M13.2 8.3l3.6 6.4M8.5 17h7"/>');
  connectionsButton.title = "Connections: files placed by their analyzed references";
  connectionsButton.dataset.spatialLayout = "connections";
  const structureButton = iconButton("Structure", '<circle cx="12" cy="5" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M12 7.5v4M12 11.5l-6 4M12 11.5l6 4"/>');
  structureButton.title = "Structure: files placed by their declared directories";
  structureButton.dataset.spatialLayout = "structure";
  for (const button of [connectionsButton, structureButton]) {
    button.addEventListener("click", () => changeSpatialLayout(button.dataset.spatialLayout === "structure" ? "structure" : "connections"));
  }
  const hopsSelect = document.createElement("select");
  hopsSelect.setAttribute("aria-label", "Hops shown");
  hopsSelect.title = "How far to explore from your pins or discovery; fewer hops reveal a smaller neighborhood";
  const hopsRange = requireElement<HTMLInputElement>("tuning-force-hops");
  for (let hops = Number(hopsRange.min); hops <= Number(hopsRange.max); hops += Number(hopsRange.step)) {
    hopsSelect.add(new Option(`${hops} ${hops === 1 ? "hop" : "hops"}`, String(hops)));
  }
  hopsSelect.addEventListener("change", () => {
    historyControl.pause();
    state.tuning.forceGraph.hops = Number(hopsSelect.value);
    syncTuningControls();
    schedulePersistUi();
    renderCurrentView();
    forceGraphView.fit();
    persistExploration();
  });
  // The history: beside the 3D layout's buttons for now, as those went beside 3D, until the consolidation pass the
  // owner expects decides where the control lives (the plan's C4). Offered only when the bundle carries a history.
  const historyButton = iconButton("History", '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>');
  historyButton.title = "History: scrub the picture through the commits";
  historyButton.addEventListener("click", () => {
    // Opening the history with nothing asked about shows the whole picture, since a repository's history is the whole
    // repository's (the owner, 2026-11-02); with a question, the mode that is on stands, and the toggle changes it.
    if (!historyControl.isOpen() && nothingAsked() && !state.openDirectories?.has("")) openRetainedDirectory("");
    historyControl.toggle();
  });
  const fitButton = iconButton("Fit current scope", '<path d="M9 3H3v6m12-6h6v6M3 15v6h6m12-6v6h-6"/>');
  fitButton.title = "Fit the current path or retained scope";
  fitButton.addEventListener("click", () => forceGraphView.fit());
  // One group per choice, parted by rules: the fit, the whole or local picture and the tuning, 2D or 3D, the 3D layout, the history.
  const group = (...buttons: HTMLElement[]): HTMLElement => {
    const element = document.createElement("span");
    element.className = "perspective-group";
    element.append(...buttons);
    return element;
  };
  perspectiveControls.append(group(fitButton), group(wholeButton, tuningButton), group(mapButton, graphButton),
    group(connectionsButton, structureButton, hopsSelect), group(historyButton));
  requireElement("main").append(perspectiveControls);
  const pinTray = createPinTray({
    parent: requireElement("main"),
    nodes: nodesById,
    onRelease: item => applyRetentionChange(releaseRetained(retentionState(), item)),
    onClearDirectory: directory => applyRetentionChange(
      clearRetainedDirectory(retentionState(), directory, retainedPath),
      !!state.selectedNode && under(shownMoment?.fileAt(state.selectedNode.id) ?? state.selectedNode.codeRelativePath, shownMoment?.directoryAt(directory) ?? directory)
    ),
    onClear: () => applyRetentionChange({ pins: EMPTY_PIN_SET, openDirectories: new Set(), retentionOrder: [] }, true),
    onFocus: focusRetained,
    onOpen: () => closeInspection(),
    canClear: () => !!state.discovery || !!state.selectedNode
  });
  // One search for the whole Explorer, in place of the Ctrl+P dialog and the FROM/TO toolbar (the owner's ask, 2026-10-14).
  const searchIndex = searchIndexOf(graphData.nodes);
  let historicalSearch: { moment: HistoryMoment; index: ReturnType<typeof searchIndexOf> } | null = null;
  const searchBar = createSearchBar({
    index: searchIndex,
    onChooseSubject: pinHit,
    onChooseDestination: askPath,
    onClearDestination: () => { leavePath(); persistExploration(); },
    // A hop is inspected at the moment that supplies the path, even when the file is gone today.
    onHopClick: node => {
      const today = shownMoment ? shownMoment.fileReference(node.id) : node.id;
      const now = today === null ? undefined : nodesById.get(today);
      if (now) handleNodeClick(now);
    }
  });
  for (const input of document.querySelectorAll<HTMLInputElement>("#search-subject, #search-destination")) {
    input.addEventListener("focus", () => historyControl.pause());
  }
  let syncTuningControls = (): void => {};
  // What a path from the newest pin can reach, computed once per newest pin and moment shown: the destination field offers only that.
  let reachKey: string | null = null;
  let reach: Reach | null = null;

  function retentionState(): RetainedState {
    return { pins: state.pins ?? EMPTY_PIN_SET, openDirectories: state.openDirectories ?? new Set(), retentionOrder: state.retentionOrder };
  }

  /** A retained identity's displayed path, for directory membership even when its portable id names a past lifetime. */
  function retainedPath(item: RetainedItem): string {
    return item.kind === "directory" ? shownMoment?.directoryAt(item.directory) ?? item.directory
      : shownMoment?.fileAt(item.pin.nodeId) ?? nodesById.get(item.pin.nodeId)?.codeRelativePath ?? item.pin.nodeId;
  }

  /** Apply a card, search, 3D or history pin with one policy; the 2D view only keeps its visual anchor. */
  function changePinned(nodeId: string, symbol: string, nextPins: PinSet, closeExpansion = false, keepSubject = false): void {
    const previousPins = state.pins ?? EMPTY_PIN_SET;
    let previousSelected = state.selectedNode;
    if (closeExpansion && previousSelected?.id === nodeId) {
      const next = nextPins.entries.at(-1);
      previousSelected = next ? nodesById.get(next.nodeId) ?? null : null;
    }
    const selectedId = previousSelected
      ? keepSubject ? previousSelected.id : subjectAfterRelease(previousPins, nextPins, previousSelected.id)
      : null;
    const selected = selectedId ? nodesById.get(selectedId) ?? null : null;
    const frame = localView.capturePinChange(nodeId, symbol, selected?.id ?? null, closeExpansion);
    state.selectedNode = selected;
    state.pins = nextPins;
    localView.clearPathForExploration();
    localView.render();
    frame.afterRender();
    persistExploration(frame.leavingPath);
    if (state.view === "map" && state.perspective === "3d") forceGraphView.render();
  }

  /** Search and history explicitly retain every interface of a file. */
  function retainNode(nodeId: string): void {
    pressPin(nodeId, "*");
  }

  /** Search retains one symbol unless it already stands alone or under a whole-file pin. */
  function retainSymbol(nodeId: string, symbol: string): void {
    const pins = state.pins ?? EMPTY_PIN_SET;
    if (isSymbolPinned(pins, nodeId, symbol) || isSymbolPinned(pins, nodeId, "*")) return;
    pressPin(nodeId, symbol);
  }

  /** A card press narrows, adds, releases or falls back by the existing pure pin grammar. */
  function pressPin(nodeId: string, symbol: string): void {
    if (!nodesById.has(nodeId)) return;
    const pins = state.pins ?? EMPTY_PIN_SET;
    const next = symbol === "*" ? retainFile(pins, nodeId) : pressSymbol(pins, nodeId, symbol);
    if (next === pins && !localView.getActivePath()) return;
    changePinned(nodeId, symbol, next);
  }

  /** The card's X releases all its pins and its 2D expansion, preserving any other retained file. */
  function closePinnedNode(nodeId: string): void {
    if (state.discovery?.kind === "file" && state.discovery.nodeId === nodeId) state.discovery = null;
    changePinned(nodeId, "*", removePinsForNode(state.pins ?? EMPTY_PIN_SET, nodeId), true);
  }

  /**
   * A pressed Pin pressed again undoes itself: the file's pins go, and the subject and the card's expansion stay, so
   * Pin then Unpin leaves the map as it was. Releasing a file together with the subject is the close's and the tray's.
   */
  function unpinNode(nodeId: string): void {
    const pins = state.pins ?? EMPTY_PIN_SET;
    const next = removePinsForNode(pins, nodeId);
    if (next !== pins) changePinned(nodeId, "*", next, false, true);
  }

  /** A 3D press adds a shared file pin; another press withdraws this file's own contribution to the neighborhood. */
  function toggleSpatialNode(nodeId: string): void {
    historyControl.pause();
    const retained = (state.pins ?? EMPTY_PIN_SET).entries.some(pin => pin.nodeId === nodeId);
    const discovered = state.discovery?.kind === "file" && state.discovery.nodeId === nodeId;
    if (retained || discovered) {
      releaseSpatialNode(nodeId);
    } else retainNode(nodeId);
  }

  /** The explicit Pin button retains an inspected file; only a subsequent Unpin withdraws it. */
  function toggleSpatialPin(nodeId: string): void {
    historyControl.pause();
    if ((state.pins ?? EMPTY_PIN_SET).entries.some(pin => pin.nodeId === nodeId)) releaseSpatialNode(nodeId);
    else retainNode(nodeId);
  }

  function releaseSpatialNode(nodeId: string): void {
    if (state.inspection?.kind === "file" && state.inspection.nodeId === nodeId) {
      state.inspection = null;
      hideInspection();
    }
    closePinnedNode(nodeId);
  }

  /** Directory discovery is reversible without implicitly retaining every descendant. Open remains explicit. */
  function toggleSpatialDirectory(directory: string): void {
    if (state.discovery?.kind !== "directory" || state.discovery.directory !== directory) {
      openInspection({ kind: "directory", directory });
      return;
    }
    historyControl.pause();
    state.discovery = null;
    if (state.inspection?.kind === "directory" && state.inspection.directory === directory) {
      state.inspection = null;
      hideInspection();
    }
    forceGraphView.render();
    persistExploration();
  }

  /** An opened directory is shared state; the Local Map owns only its screen anchor and focus. */
  function changeRetainedDirectory(directory: string, next: OpenDirectories): void {
    const frame = localView.captureDirectoryChange(directory);
    state.openDirectories = next;
    localView.clearPathForExploration();
    localView.render();
    frame.afterRender();
    persistExploration(frame.leavingPath);
    if (state.view === "map" && state.perspective === "3d") forceGraphView.render();
  }

  function openRetainedDirectory(directory: string): void {
    const current = state.openDirectories ?? new Set<string>();
    const next = addOpenedDirectory(current, directory);
    if (next !== current || localView.getActivePath()) changeRetainedDirectory(directory, next);
  }

  function closeRetainedDirectory(directory: string): void {
    const current = state.openDirectories ?? new Set<string>();
    const next = closeOpenedDirectory(current, directory);
    if (next !== current) changeRetainedDirectory(directory, next);
  }

  function applyRetentionChange(next: RetainedState, releaseSubject = false): void {
    if (releaseSubject) state.discovery = null;
    const current = state.selectedNode;
    const selectedId = subjectAfterRelease(state.pins ?? EMPTY_PIN_SET, next.pins, current?.id ?? null, releaseSubject);
    const selected = selectedId ? nodesById.get(selectedId) ?? null : null;
    const focused = selected;
    const view = state.perspective === "2d" ? localView : forceGraphView;
    const anchor = focused ? view.getSubjectAnchor(focused.id) : null;
    Object.assign(state, next);
    state.selectedNode = selected;
    persistExploration(!!localView.getActivePath());
    renderCurrentView();
    if (releaseSubject && state.perspective === "3d") forceGraphView.fit();
    if (focused && anchor) view.placeSubjectAnchor(focused.id, anchor);
  }

  function focusRetained(item: RetainedItem): void {
    closeInspection(false);
    if (item.kind === "directory") {
      (state.perspective === "2d" ? localView : forceGraphView).focusDirectory(item.directory);
      persistExploration();
      return;
    }
    const node = nodesById.get(item.pin.nodeId);
    if (!node) return;
    if (historyControl.absence(item) && state.perspective === "3d") historyControl.showFile(node.id);
    if (state.perspective === "2d") localView.focusNode(node.id);
    else forceGraphView.focusNode(node.id);
    persistExploration();
  }

  /** The words a field shows for a retained thing: its name, and where it stands for the tooltip. */
  function wordsOf(item: RetainedItem): SearchWords {
    const words = describeRetained(item, id => nodesById.get(id)?.name, id => nodesById.get(id)?.codeRelativePath ?? id);
    return { name: words.name, context: words.description };
  }

  /** A field's words with, while the picture shows a moment, the tray's words for a thing that does not stand there. */
  function chipWordsOf(item: RetainedItem): SearchWords {
    const absence = historyControl.absence(item);
    const moment = momentWords();
    const at = item.kind === "pin" ? shownMoment?.fileAt(item.pin.nodeId) : null;
    const retained = wordsOf(item);
    const specific = item.kind === "pin" && item.pin.symbol !== "*";
    const words = at ? { name: specific ? retained.name : at.slice(at.lastIndexOf("/") + 1),
      context: specific ? `${retained.name} in ${at}` : at } : retained;
    return { ...words, ...(absence ? { absence: { words: absence, moment: moment ? `The picture shows ${moment}` : "The picture shows today's bundle" } } : {}) };
  }

  /** A destination as the retained item it would be once pinned, for its words, its files and the reverse question. */
  function itemOf(destination: PathDestination): RetainedItem {
    return destination.kind === "directory" ? { kind: "directory", directory: destination.directory }
      : { kind: "pin", pin: { nodeId: destination.nodeId, symbol: destination.symbol ?? "*" } };
  }

  /** A retained item as the destination it would be, for the reverse question. */
  function destinationOfItem(item: RetainedItem): PathDestination {
    if (item.kind === "directory") return { kind: "directory", directory: item.directory };
    return item.pin.symbol === "*" ? { kind: "file", nodeId: item.pin.nodeId } : { kind: "file", nodeId: item.pin.nodeId, symbol: item.pin.symbol };
  }

  function destinationOfHit(hit: SearchHit): PathDestination {
    if (hit.kind === "directory") return { kind: "directory", directory: shownMoment?.directoryToday(hit.directory) ?? hit.directory };
    const nodeId = shownMoment && shownMoment.graph.nodes.some(node => node.id === hit.node.id)
      ? shownMoment.fileReference(hit.node.id) : hit.node.id;
    return hit.kind === "symbol" ? { kind: "file", nodeId, symbol: hit.symbol } : { kind: "file", nodeId };
  }

  /** Make a retained item the newest, so that a path starts from it. */
  function promoteRetained(item: RetainedItem): void {
    const key = retainedKey(item);
    state.retentionOrder = [...(state.retentionOrder ?? []).filter(other => other !== key), key];
  }

  /**
   * Pin what a search found and bring it into view: a file with all its symbols, one symbol, or a directory opened.
   * The chosen thing becomes the newest pin. A destination already asked is asked again from it.
   */
  function pinHit(hit: SearchHit): void {
    state.discovery = null;
    // Search rows use the displayed moment's names. Selection uses a lifetime-stable id across moments.
    const destination = destinationOfHit(hit);
    if (destination.kind === "directory") hit = { kind: "directory", directory: destination.directory };
    else {
      const node = nodesById.get(destination.nodeId);
      if (!node) return;
      hit = destination.symbol ? { kind: "symbol", node, symbol: destination.symbol } : { kind: "file", node };
    }
    const asked = state.destination ?? null;
    closeInspection(false);
    if (hit.kind === "directory") {
      if (state.view !== "map") changePerspective(state.perspective);
      openRetainedDirectory(hit.directory);
      promoteRetained({ kind: "directory", directory: hit.directory });
      (state.perspective === "2d" ? localView : forceGraphView).focusDirectory(hit.directory);
    } else {
      // From another view the Local Map opens on it; inside the map it becomes the subject and the view brings it to the eye.
      if (state.view !== "map") changePerspective(state.perspective, hit.node);
      else if (state.selectedNode?.id !== hit.node.id) markSelected(hit.node);
      if (hit.kind === "file") retainNode(hit.node.id);
      else retainSymbol(hit.node.id, hit.symbol);
      const pin = (state.pins ?? EMPTY_PIN_SET).entries.find(entry => entry.nodeId === hit.node.id
        && (hit.kind === "file" ? entry.symbol === "*" : entry.symbol === hit.symbol || entry.symbol === "*"));
      if (pin) promoteRetained({ kind: "pin", pin });
      if (state.perspective === "2d") localView.focusNode(hit.node.id, hit.kind === "symbol" ? hit.symbol : undefined);
      else forceGraphView.render();
    }
    state.destination = asked;
    if (asked) runPath();
    else persistExploration();
  }

  /** Ask for the path from the newest pin to what was chosen; asking is a step in the history. */
  function askPath(hit: SearchHit): void {
    state.destination = destinationOfHit(hit);
    runPath();
  }

  /** Leave the path: the destination forgotten, the exploration shown again. */
  function leavePath(): void {
    state.destination = null;
    clearPathResult();
  }

  /** Draw the path from the newest pin to the destination, or say why none is drawn; nothing asked clears the report. */
  function runPath(): void {
    const origin = newestRetained(retentionState());
    const destination = state.destination ?? null;
    if (!origin || !destination) {
      clearPathResult();
      persistExploration();
      return;
    }
    reportPath(origin, destination, true);
    persistExploration();
  }

  /**
   * Ask the path over the ground the picture shows and report it: at a moment of the history over that moment's files
   * and wires, the report naming the commit, and an end that does not stand there said so instead of a path
   * (2026-10-30). `draw` draws the view after; a move of the history draws the picture itself.
   */
  function reportPath(origin: RetainedItem, destination: PathDestination, draw: boolean): void {
    const target = itemOf(destination);
    const ground = pathGround();
    const absent = [origin, target].flatMap(item => {
      const words = historyControl.absence(item);
      return words ? [`${wordsOf(item).name}, ${words}`] : [];
    });
    if (absent.length > 0) {
      clearPathResult(draw);
      searchBar.setStatus(absent.join("; "), "", reportAt());
      if (draw) renderCurrentView("show");
      return;
    }
    const result = findPath(endFiles(origin, ground), endFiles(target, ground), ground.nodesById, ground.links);
    const first = result.path[0];
    const last = result.path.at(-1);
    if (origin.kind === "pin" && origin.pin.symbol !== "*" && first && first.nodeId === ground.fileAt(origin.pin.nodeId)) first.symbol = origin.pin.symbol;
    if (destination.kind === "file" && destination.symbol && last && last.nodeId === ground.fileAt(destination.nodeId)) last.symbol = destination.symbol;
    showPathResult(result, wordsOf(origin), wordsOf(target), ground, draw);
  }

  /** The exploration with the destination pinned as the newest item: the state the reverse question leads to. */
  function retainedWithDestination(destination: PathDestination): RetainedState {
    const item = itemOf(destination);
    const pins = state.pins ?? EMPTY_PIN_SET;
    return reconcileRetained({
      pins: item.kind === "pin" ? (item.pin.symbol === "*" ? retainFile(pins, item.pin.nodeId) : addPin(pins, item.pin.nodeId, item.pin.symbol)) : pins,
      openDirectories: item.kind === "directory" ? new Set([...(state.openDirectories ?? []), item.directory]) : state.openDirectories ?? new Set(),
      retentionOrder: [...(state.retentionOrder ?? []).filter(key => key !== retainedKey(item)), retainedKey(item)]
    });
  }

  /** The address of the reverse question, so that the offered link can be opened elsewhere. */
  function reverseHref(): string {
    const origin = newestRetained(retentionState());
    const destination = state.destination ?? null;
    if (!origin || !destination) return "#";
    const retained = retainedWithDestination(destination);
    const url = new URL(window.location.href);
    url.searchParams.set("s", compressSnapshot({
      ...readUrlState(), view: "map", perspective: state.perspective, spatialLayout: state.spatialLayout, selectedNodeId: state.selectedNode?.id ?? null,
      pinSet: retained.pins, openDirectories: retained.openDirectories, retentionOrder: retained.retentionOrder, filters: state.filters,
      layout: layoutSettingsOf(state.tuning), destination: destinationOfItem(origin)
    }));
    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  /** The reverse question: the destination becomes the newest pin, and the path is asked back to where it started. */
  function swapPath(): void {
    const origin = newestRetained(retentionState());
    const destination = state.destination ?? null;
    if (!origin || !destination) return;
    const node = destination.kind === "file" ? nodesById.get(destination.nodeId) : undefined;
    if (destination.kind === "file" && !node) return;
    state.destination = destinationOfItem(origin);
    pinHit(destination.kind === "directory" ? { kind: "directory", directory: destination.directory }
      : destination.symbol ? { kind: "symbol", node: node!, symbol: destination.symbol } : { kind: "file", node: node! });
  }

  function syncPerspectiveControls(): void {
    const contextName = document.getElementById("context-name");
    const selectedPath = state.selectedNode ? shownMoment?.fileAt(state.selectedNode.id) ?? state.selectedNode.codeRelativePath : null;
    const discoveredPath = state.discovery?.kind === "directory" ? shownMoment?.directoryAt(state.discovery.directory) ?? state.discovery.directory
      : state.discovery?.kind === "file" ? shownMoment?.fileAt(state.discovery.nodeId) ?? nodesById.get(state.discovery.nodeId)?.codeRelativePath : null;
    if (contextName) contextName.textContent = selectedPath
      ?? ([...(state.openDirectories ?? [])].map(path => (shownMoment?.directoryAt(path) ?? path) || "Scan root").join(", ") || discoveredPath || "Scan root");
    perspectiveControls.hidden = state.view !== "map";
    for (const button of [fitButton, wholeButton, tuningButton, mapButton, graphButton, connectionsButton, structureButton, historyButton]) button.disabled = changingPerspective;
    fitButton.hidden = connectionsButton.hidden = structureButton.hidden = state.perspective !== "3d";
    hopsSelect.hidden = state.perspective !== "3d";
    hopsSelect.disabled = changingPerspective || !!localView.getActivePath();
    hopsSelect.value = String(state.tuning.forceGraph.hops);
    historyButton.hidden = state.perspective !== "3d" || !historyControl.available;
    historyButton.setAttribute("aria-pressed", String(historyControl.isOpen()));
    connectionsButton.setAttribute("aria-pressed", String(state.spatialLayout === "connections"));
    structureButton.setAttribute("aria-pressed", String(state.spatialLayout === "structure"));
    const whole = state.openDirectories?.has("") ?? false;
    wholeButton.setAttribute("aria-pressed", String(whole));
    wholeButton.title = whole ? "Whole picture on: every file is shown; press to return to what you asked about"
      : state.perspective === "3d" ? "Show the whole picture: every file, the other groups and the lone files included" : "Show the whole picture: the scan root around what you asked about";
    mapButton.setAttribute("aria-pressed", String(state.perspective === "2d"));
    graphButton.setAttribute("aria-pressed", String(state.perspective === "3d"));
    const suggest3d = state.perspective === "2d" && backwardConnections > 0;
    graphButton.classList.toggle("has-backward-wires", suggest3d);
    graphButton.title = suggest3d ? `${backwardConnections} ${backwardConnections === 1 ? "connection runs" : "connections run"} backward in 2D. Explore this scope in 3D.` : "Local Map in 3D";
    state.retentionOrder = reconcileRetained(retentionState()).retentionOrder;
    historyControl.sync({
      visible: state.view === "map" && state.perspective === "3d",
      pins: { files: [...new Set((state.pins ?? EMPTY_PIN_SET).entries.map(pin => pin.nodeId))], directories: [...(state.openDirectories ?? [])] },
      subject: state.selectedNode?.id ?? null,
      showAuthors: state.showAuthors ?? false,
      tuning: state.tuning.history
    });
    syncRetainedWords();
    syncStatsLine();
  }

  /**
   * The tray and the search bar, which name the retained things: each said absent where it does not stand at the moment
   * shown, and the destination field offering what a path from the newest pin reaches, at that moment when one is shown.
   */
  function syncRetainedWords(): void {
    if (shownMoment && historicalSearch?.moment !== shownMoment) historicalSearch = { moment: shownMoment, index: searchIndexOf(shownMoment.graph.nodes) };
    searchBar.setIndex(shownMoment ? historicalSearch!.index : searchIndex);
    pinTray.sync(retentionState(), state.view === "map", changingPerspective, item => historyControl.absence(item));
    const newest = newestRetained(retentionState());
    const key = newest ? JSON.stringify([retainedKey(newest), shownMoment?.step ?? null]) : null;
    if (key !== reachKey) {
      reachKey = key;
      const ground = pathGround();
      reach = newest ? reachOf(endFiles(newest, ground), ground.nodesById, ground.links) : null;
    }
    searchBar.sync(newest ? chipWordsOf(newest) : null, state.destination ? chipWordsOf(itemOf(state.destination)) : null, reach, state.view === "map");
  }

  /** Change how the 3D Local Map places its files; the picture re-settles from where it stands, a step in the history. */
  function changeSpatialLayout(layout: SpatialLayout): void {
    if (state.spatialLayout === layout || changingPerspective) return;
    state.spatialLayout = layout;
    renderCurrentView();
    const subject = state.selectedNode;
    const singleFile = subject && !state.openDirectories?.size && !localView.getActivePath()
      && (state.pins?.entries ?? []).every(pin => pin.nodeId === subject.id)
      && (!state.discovery || state.discovery.kind === "file" && state.discovery.nodeId === subject.id);
    if (singleFile) forceGraphView.focusNode(subject.id);
    else forceGraphView.fit();
    persistExploration();
  }

  let changingPerspective = false;
  let pendingPerspective: (() => void) | null = null;
  let perspectiveEpoch = 0;
  let transitionDestination: LocalMapPerspective | null = null;
  let releasePerspectiveCover = (): void => {};
  let cancelPerspectiveAnimation = (): void => {};

  /** Change representation around the map subject; the independently inspected evidence stays open. */
  function changePerspective(perspective: LocalMapPerspective, target = state.selectedNode): void {
    if (changingPerspective) { pendingPerspective = () => changePerspective(perspective, target); return; }
    const subjectChanged = target?.id !== state.selectedNode?.id;
    if (state.view === "map" && state.perspective === perspective && !subjectChanged) return;
    const epoch = ++perspectiveEpoch;
    transitionDestination = perspective;
    const previous = state.view === "map" ? state.perspective : null;
    const destination = perspective === "2d" ? "map" : "graph";
    const sourceSurface = previous === "2d" ? "map" : "graph";
    const sourceCards = previous === "2d" ? captureLocalScene(requireElement("view-map")) : null;
    const sourceGraph = previous === "3d" ? forceGraphView.captureScene() : null;
    const releaseCover = previous ? holdPerspective(requireElement(`view-${sourceSurface}`)) : () => {};
    releasePerspectiveCover = releaseCover;
    changingPerspective = true;
    syncPerspectiveControls();
    const source = previous === "2d" ? localView : previous === "3d" ? forceGraphView : null;
    // A pin-only exploration has an identity to hold too, without turning that pin into the selected subject.
    const visualId = target?.id ?? state.pins?.entries.at(-1)?.nodeId;
    let anchor = visualId && source ? source.getSubjectAnchor(visualId) : null;
    // The 2D map draws today's docs: leaving 3D returns the picture to today and closes the history.
    if (perspective === "2d") historyControl.leave();
    state.selectedNode = target;
    state.view = "map";
    state.perspective = perspective;
    setActiveView(state.view, perspective);
    renderCurrentView(subjectChanged ? "change" : "show");
    if (perspective === "3d" && sourceCards) forceGraphView.revealForTransition(sourceCards.cards.map(card => card.file.id));
    persistExploration();
    if (visualId && anchor) {
      const viewport = requireElement(`view-${destination}`).getBoundingClientRect();
      if (anchor.x >= viewport.left && anchor.x <= viewport.right && anchor.y >= viewport.top && anchor.y <= viewport.bottom) {
        (perspective === "2d" ? localView : forceGraphView).placeSubjectAnchor(visualId, anchor);
      } else anchor = null;
    }
    const ready = perspective === "3d" ? forceGraphView.whenReady() : Promise.resolve();
    void ready.then(() => {
      if (epoch !== perspectiveEpoch) { releaseCover(); return; }
      if (visualId && anchor) (perspective === "2d" ? localView : forceGraphView).placeSubjectAnchor(visualId, anchor);
      if (previous && previous !== perspective) {
        const cards = perspective === "3d" ? sourceCards! : captureLocalScene(requireElement("view-map"));
        const graph = perspective === "3d" ? forceGraphView.captureScene() : sourceGraph!;
        forceGraphView.setActive(false);
        const named = visualId ?? "";
        cancelPerspectiveAnimation = animatePerspective(cards, graph, perspective === "3d", named, requireElement(`view-${destination}`).getBoundingClientRect(), () => {
          if (epoch !== perspectiveEpoch) return;
          transitionDestination = null;
          changingPerspective = false;
          syncPerspectiveControls();
          forceGraphView.setActive(state.view === "map" && state.perspective === "3d");
          // Back in 2D, the subject was held where its sphere stood through the motion; the picture now slides into
          // the frame where it fits, one motion the eye can follow (the owner's choice, 2026-10-30).
          if (perspective === "2d") localView.settleIntoFrame();
          const pending = pendingPerspective; pendingPerspective = null; pending?.();
        });
      } else {
        transitionDestination = null;
        changingPerspective = false;
        syncPerspectiveControls();
      }
      releaseCover();
    });
    highlightSelectedCards();
  }

  // The page's controls are wired here, never by attributes in its HTML, which its Content-Security-Policy refuses to run.
  for (const button of document.querySelectorAll<HTMLButtonElement>("#sidebar .nav-item[data-view]")) {
    const viewName = button.dataset.view as ViewName;
    button.addEventListener("click", event => {
      event.preventDefault();
      if (viewName === "map") { changePerspective(state.perspective); return; }
      // The other views draw today's docs: leaving 3D returns the picture to today and closes the history.
      historyControl.leave();
      setActiveView(viewName, state.perspective);
      state.view = viewName;
      renderCurrentView("show");
      persistExploration();
    });
  }

  requireElement("download-doc-btn").addEventListener("click", () => detailPanel.downloadCurrentDoc());

  const zooms: Record<string, () => void> = {
    in: () => localView.zoomIn(),
    out: () => localView.zoomOut(),
    reset: () => localView.resetZoom()
  };
  for (const button of document.querySelectorAll<HTMLButtonElement>("#controls [data-zoom]")) {
    const zoom = zooms[button.dataset.zoom ?? ""];
    button.addEventListener("click", () => {
      if (state.view !== "map" || state.perspective !== "2d") return;
      zoom();
      localView.drawConnections();
    });
  }

  requireElement("sidebar-toggle").addEventListener("click", () => {
    sidebar.classList.toggle("collapsed");
    syncSidebarToggle();
  });

  function syncSidebarToggle(): void {
    const collapsed = sidebar.classList.contains("collapsed");
    const button = requireElement<HTMLButtonElement>("sidebar-toggle");
    button.textContent = collapsed ? "▶" : "◀";
    button.setAttribute("aria-expanded", String(!collapsed));
    button.setAttribute("aria-label", collapsed ? "Open navigation" : "Collapse navigation");
    button.title = collapsed ? "Open navigation" : "Collapse navigation";
  }

  window.addEventListener("resize", () => {
    syncReaderPresentation();
    if (state.view === "map" && state.perspective === "2d") {
      localView.drawConnections();
    }
  });

  // ==================
  // PATHS
  // ==================

  let restoringPath = false;

  /**
   * Shows what the search found. The Local Map draws a path only in its reading
   * direction, the start offering on the left and the end using on the right. When the
   * files connect only the other way, nothing is drawn: the status says so and
   * offers the reverse question as a link, the owner's rule of 2025-12-18.
   */
  const showPathResult = (result: PathfindResult, origin: SearchWords, destination: SearchWords, ground: PathGround, draw: boolean): void => {
    const fromName = origin.name;
    const toName = destination.name;

    // A question asked through the global bar opens the Local Map for every outcome. Restoring a saved place keeps
    // its chosen view, where the question is retained and the report stays hidden until the person returns to the map.
    if (!restoringPath && state.view !== "map") {
      state.view = "map";
      setActiveView("map", state.perspective);
    }

    if (result.path.length > 0) {
      // Reading evidence is independent of the path being drawn.

      // Path mode draws over a selected file; when none is selected yet, the
      // path's first file becomes it. An existing selection is kept, so Clear
      // returns to the place the person was, address included.
      if (!state.selectedNode) {
        // The first file as it stands today: at a moment, a hop is named as the moment names it.
        const today = shownMoment ? shownMoment.fileReference(result.path[0].nodeId) : result.path[0].nodeId;
        const node = today === null ? undefined : nodesById.get(today);
        if (node && draw) selectNode(node, { suppressDetailPanel: true });
        else if (node) markSelected(node);
      }

      searchBar.showPath(result.path);

      const nodeIds = result.path.map(hop => hop.nodeId);
      localView.setActivePath({
        nodeIds,
        fromSymbol: result.path[0].symbol,
        toSymbol: result.path[result.path.length - 1].symbol
      });

      const against = referencesAgainstPath(nodeIds, ground.links);
      const notDrawn = against === 0
        ? ""
        : `; ${against} ${against === 1 ? "reference" : "references"} between these files ${against === 1 ? "runs" : "run"} the other way and ${against === 1 ? "is" : "are"} not drawn in 2D`;
      if (draw) renderCurrentView();
      searchBar.setStatus(`Path found: ${result.path.length} files${notDrawn}`, "success", reportAt());
      return;
    }

    // Nothing drawn: a path shown before leaves, and the status says why
    clearPathResult(draw);

    if (result.reversePath.length > 0) {
      const statusEl = searchBar.setStatus(
        `No path runs from ${fromName} to ${toName}. ${toName} reaches ${fromName} through ${result.reversePath.length} files: `,
        "",
        reportAt()
      );
      const reverse = document.createElement("a");
      reverse.href = reverseHref();
      reverse.textContent = `show ${toName} to ${fromName}`;
      reverse.addEventListener("click", event => {
        event.preventDefault();
        swapPath();
      });
      statusEl.appendChild(reverse);
      if (draw) renderCurrentView("show");
      return;
    }

    searchBar.setStatus(
      result.maxDepthReached
        ? `No path within ${Math.max(0, ground.nodesById.size - 1)} hops either way (searched ${result.searchedNodes} files)`
        : `No path either way (searched ${result.searchedNodes} files)`,
      "error",
      reportAt()
    );
    if (draw) renderCurrentView("show");
  };

  /** Take the path's report off the page and the path out of the Local Map; the destination asked is kept by the caller. `draw` redraws the 3D picture. */
  const clearPathResult = (draw = true): void => {
    searchBar.clearPath();
    localView.setActivePath(null);
    if (draw && state.view === "map" && state.perspective === "3d") forceGraphView.render();
  };

  // The node to focus first, chosen before the first drawing so that the page draws the focused picture once, rather
  // than a default picture and then the focused one.
  const urlRequestedNodeId = initialState.hasUrlState ? initialState.nodeId : null;
  const storedOrConfiguredNodeId = !initialState.hasUrlState ? (persistedNav?.nodeId ?? initialState.nodeId) : null;
  // An address that opens a directory and names no file is the door into that directory: nothing is put in focus for it.
  const enteredByDirectory = !initialState.nodeId && !persistedNav?.nodeId && state.openDirectories.size > 0;

  const initialFocusNodeId = (() => {
    if (enteredByDirectory || (new URLSearchParams(window.location.search).has("s") && !initialState.nodeId)) {
      return null;
    }
    if (urlRequestedNodeId) {
      return urlRequestedNodeId;
    }
    if (storedOrConfiguredNodeId && nodesById.has(storedOrConfiguredNodeId)) {
      return storedOrConfiguredNodeId;
    }
    // The docs' entry points decide the landing: the one entry outside the sample roots, or the root when there are
    // several or none a reading starts at; the guess by file name only when the docs declare no entry at all.
    const landing = landingOf(bundle.graph, bundle.sampleRoots ?? []);
    if (landing.kind === "file") return landing.codePath;
    if (landing.kind === "root") return null;
    return inferDefaultEntryNodeId(graphData, resolveLinkEndpoint, nodesById);
  })();
  const focusNode = initialFocusNodeId ? nodesById.get(initialFocusNodeId) : undefined;
  if (focusNode) {
    markSelected(focusNode);
  } else if (initialFocusNodeId) {
    console.warn(`Initial focus node not found: ${initialFocusNodeId}`);
  }

  // Set initial sidebar active state based on parsed URL/config
  setActiveView(state.view, state.perspective);

  // Render initial view
  // Resolve a shared path before drawing, so loading it never paints an unrelated neighborhood first.
  if (state.destination) {
    restoringPath = true;
    try { runPath(); } finally { restoringPath = false; }
  }
  // An address that names a commit opens the 3D picture on that moment, before the first drawing.
  if (state.historyCommit && state.view === "map" && state.perspective === "3d") void historyControl.showCommit(state.historyCommit);
  renderCurrentView();
  renderInspection();

  if (focusNode) {
    setTimeout(highlightSelectedCards, 100);
  }

  syncTuningControls = initTuningPanel({
    state,
    onTuningChange: () => { schedulePersistUi(); persistExploration(); },
    onRender: () => {
      // The spatial renderer owns both file and directory camera anchors.
      if (state.perspective === "3d") {
        renderCurrentView();
        return;
      }
      // An explicit tuning action owns the camera from this point, including
      // when the initial automatic framing has not finished yet.
      const subject = state.selectedNode;
      const view = localView;
      const anchor = subject ? view.getSubjectAnchor(subject.id) : null;
      if (subject && anchor) view.placeSubjectAnchor(subject.id, anchor);
      renderCurrentView();
      if (subject && anchor) view.placeSubjectAnchor(subject.id, anchor);
    },
    drawLocalConnections: () => localView.drawConnections(),
  });

  // Record the actual initial subject and reader before arming history, including plain and remembered entry links.
  persistExploration();
  // Back and Forward: a move to another place is an entry, and landing on one shows the place its address names.
  startHistory({ placeOf, restore: restoreFromUrl });
  const backButton = document.getElementById("history-back");
  const forwardButton = document.getElementById("history-forward");
  const readerBack = requireElement<HTMLButtonElement>("reader-back");
  const readerForward = requireElement<HTMLButtonElement>("reader-forward");
  const syncHistoryButtons = (): void => {
    if (backButton instanceof HTMLButtonElement) {
      backButton.disabled = !canGoBack();
    }
    if (forwardButton instanceof HTMLButtonElement) {
      forwardButton.disabled = !canGoForward();
    }
    readerBack.disabled = !canGoBack();
    readerForward.disabled = !canGoForward();
  };
  onHistoryChange(syncHistoryButtons);
  syncHistoryButtons();
  backButton?.addEventListener("click", () => window.history.back());
  forwardButton?.addEventListener("click", () => window.history.forward());
  readerBack.addEventListener("click", () => window.history.back());
  readerForward.addEventListener("click", () => window.history.forward());
  readerRoom.addEventListener("change", syncReaderPresentation);
  document.addEventListener("keydown", event => {
    if (event.defaultPrevented || !state.inspection) return;
    if (event.key === "Escape") {
      // An open popover, the ancestry chooser, stands above the reader; the browser closes it on this same Escape.
      if (document.querySelector(":popover-open")) return;
      event.preventDefault();
      closeInspection();
      return;
    }
    if (event.key !== "Tab" || !readerRoom.matches) return;
    const controls = [...requireElement("detail-panel").querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]'
    )].filter(element => !element.closest("[hidden], [inert]") && element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && (document.activeElement === first || !requireElement("detail-panel").contains(document.activeElement))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  });

  /** Shows the place the address names, after Back or Forward; the history turns any write it makes into a rewrite of that entry. */
  async function restoreFromUrl(): Promise<void> {
    const address = window.location.href;
    const requested = readUrlState();
    historyControl.pause();
    await historyControl.restoreReferences(selectedFileIds(requested), !!requested.commit);
    // A second Back/Forward or deliberate navigation may have superseded the request while the sequence loaded.
    if (window.location.href !== address) return;
    const place = parseInitialState();
    const view: ViewName = place.hasUrlState ? place.view : bundle.board ? "world" : place.view;
    const exploration = scrubSnapshot({ ...requested, openDirectories: place.openDirectories }, nodesById, bundledMarkdown, directory => historyControl.hasDirectory(directory));
    const nextTuning = applyLayoutSettings(state.tuning, exploration.layout);
    const nextFilters = new URLSearchParams(window.location.search).has("s")
      ? { ...state.filters, ...exploration.filters } : state.filters;
    const nextMap = {
      ...exploration,
      view,
      perspective: place.perspective ?? state.perspective,
      spatialLayout: place.spatialLayout ?? state.spatialLayout,
      selectedNodeId: place.nodeId ?? null,
      layout: layoutSettingsOf(nextTuning),
      filters: nextFilters,
      inspection: null
    };
    itemMenu.close();
    // Evidence-only Back/Forward leaves camera, DOM identity and the running layout search untouched.
    if (compressSnapshot(nextMap) === compressSnapshot({ ...explorationSnapshot(), inspection: null })) {
      state.inspection = exploration.inspection ?? null;
      renderInspection();
      schedulePersistNav();
      return;
    }
    state.view = view;
    state.perspective = place.perspective ?? state.perspective;
    state.spatialLayout = place.spatialLayout ?? state.spatialLayout;
    state.inspection = exploration.inspection ?? null;
    state.discovery = exploration.discovery ?? (state.inspection?.kind === "file" ? { kind: "file", nodeId: state.inspection.nodeId }
      : state.inspection?.kind === "directory" ? state.inspection : null);
    state.tuning = nextTuning;
    state.filters = nextFilters;
    syncFilterControls();
    state.pins = exploration.pinSet;
    state.openDirectories = exploration.openDirectories;
    state.retentionOrder = exploration.retentionOrder;
    setActiveView(view, state.perspective);
    const node = place.nodeId ? nodesById.get(place.nodeId) ?? null : null;
    state.selectedNode = node;
    const contextName = document.getElementById("context-name");
    if (node && contextName instanceof HTMLElement) {
      contextName.textContent = node.codeRelativePath;
    }
    state.destination = exploration.destination;
    state.reachFrom = exploration.reachFrom;
    state.showAuthors = exploration.authors;
    // Path restoration can persist synchronously; it must already see the restored moment.
    state.historyCommit = exploration.commit;
    clearPathResult();
    if (state.destination) {
      restoringPath = true;
      try { runPath(); } finally { restoringPath = false; }
    }
    renderCurrentView();
    // The moment the address names, or today; the history turns the write this makes into a rewrite of the entry.
    if (view === "map" && state.perspective === "3d") await historyControl.showCommit(exploration.commit);
    else historyControl.leave();
    schedulePersistNav();
    highlightSelectedCards();
    renderInspection();
  }

  interface SelectNodeOptions {
    /** If true, suppress opening the detail panel */
    suppressDetailPanel?: boolean;
  }

  /** The node the person is looking at: the state, the context name, the address and the stored place, without drawing. */
  function markSelected(node: ExplorerNodePayload): void {
    state.selectedNode = node;
    const contextName = document.getElementById("context-name");
    if (contextName instanceof HTMLElement) {
      contextName.textContent = node.codeRelativePath;
    }
  }

  function selectNode(node: ExplorerNodePayload, options?: SelectNodeOptions): void {
    markSelected(node);
    renderCurrentView();
    highlightSelectedCards();
    if (!options?.suppressDetailPanel) {
      focusSidebar(node);
    }
  }

  function focusSidebar(node: ExplorerNodePayload, symbol?: string): void {
    openInspection({ kind: "file", nodeId: node.id, ...(symbol ? { symbol } : {}) });
  }

  function handleNodeClick(node: ExplorerNodePayload): void {
    focusSidebar(node);
  }

  /** Centre is deliberate navigation: retain the working set and close the reader. */
  function recentreOn(node: ExplorerNodePayload): void {
    state.discovery = null;
    closeInspection(false);
    leavePath();
    if (state.view !== "map") changePerspective(state.perspective, node);
    else selectNode(node, { suppressDetailPanel: true });
    if (state.perspective === "3d") forceGraphView.focusNode(node.id);
    else localView.focusNode(node.id);
    persistExploration();
  }

  function renderInspection(): void {
    const target = state.inspection;
    if (!target) {
      hideInspection();
      return;
    }
    const panel = requireElement("detail-panel");
    const heldFocus = panel.contains(document.activeElement);
    if (!panel.classList.contains("visible") && document.activeElement instanceof HTMLElement
      && document.activeElement !== document.body && !panel.contains(document.activeElement)) {
      inspectionOpener = document.activeElement;
    }
    pinTray.close();
    if (target.kind === "file") {
      const node = nodesById.get(target.nodeId);
      if (node) detailPanel.showNode(node, target.symbol);
    } else if (target.kind === "directory") detailPanel.showDirectory(target.directory);
    else if (bundledMarkdown && Object.hasOwn(bundledMarkdown, target.path)) {
      detailPanel.showBundledDoc(target.path, bundledMarkdown[target.path], target.fragment);
    }
    syncInspectionActions();
    syncReaderNote();
    syncReaderPresentation();
    // A followed evidence link can replace the focused anchor on wide screens too.
    if (heldFocus && !panel.contains(document.activeElement)) requireElement("detail-close").focus({ preventScroll: true });
  }

  function actionsForFile(node: ExplorerNodePayload, symbol?: string): DetailPanelAction[] {
    const pins = state.pins ?? EMPTY_PIN_SET;
    const whole = isSymbolPinned(pins, node.id, "*");
    const pinned = symbol ? isSymbolPinned(pins, node.id, symbol) : whole;
    const part = symbol === INTERNAL_SYMBOL ? "Internals" : "symbol";
    return [
      {
        label: symbol ? (pinned ? `Unpin ${part}` : whole ? `Pin only ${part === "symbol" ? "this symbol" : part}` : `Pin ${part}`) : whole ? "Unpin file" : "Pin file",
        pressed: pinned,
        run: () => {
          if (!symbol && whole) unpinNode(node.id);
          else pressPin(node.id, symbol ?? "*");
        }
      },
      { label: `Centre on ${node.name}`, text: "Centre", run: () => recentreOn(node) }
    ];
  }

  function syncInspectionActions(): void {
    const target = state.inspection;
    if (target?.kind === "file") {
      const node = nodesById.get(target.nodeId);
      detailPanel.setActions(node ? actionsForFile(node, target.symbol) : []);
    } else if (target?.kind === "directory") {
      const opened = state.openDirectories?.has(target.directory) ?? false;
      detailPanel.setActions([
        {
          label: opened ? "Close directory" : "Open directory",
          pressed: opened,
          run: () => opened ? closeRetainedDirectory(target.directory) : openRetainedDirectory(target.directory)
        },
        { label: "Centre on directory", text: "Centre", run: () => centreOnDirectory(target.directory) }
      ]);
    } else detailPanel.setActions([]);
  }

  /**
   * While the 3D picture shows a past commit, the reader says that its evidence is today's version (2026-10-20), and,
   * in the tray's words, when the file or directory it reads does not stand at that commit (2026-10-30).
   */
  function syncReaderNote(): void {
    const target = state.inspection;
    const moment = target && state.view === "map" && state.perspective === "3d" ? momentWords() : null;
    if (!target || !moment) {
      const absent = target?.kind === "file" ? historyControl.absence({ kind: "pin", pin: { nodeId: target.nodeId, symbol: "*" } }) : null;
      detailPanel.setNote(absent ? `Not in today's bundle: ${absent}.` : null);
      return;
    }
    const absence = target.kind === "file" ? historyControl.absence({ kind: "pin", pin: { nodeId: target.nodeId, symbol: "*" } })
      : target.kind === "directory" ? historyControl.absence({ kind: "directory", directory: target.directory })
        : null;
    const structural = target.kind === "directory" || target.kind === "file" && !Object.hasOwn(files, target.nodeId);
    detailPanel.setNote(structural
      ? `Structure at ${moment}${absence ? `: ${absence}` : ""}.${target.kind === "file" ? " Historical text is not bundled." : ""}`
      : `Today's version. The picture shows ${moment}${absence ? `: ${absence}` : ""}.`);
  }

  /** A full reader covers a small viewport; opening it never resizes the underlying map. */
  function syncReaderPresentation(): void {
    const panel = requireElement("detail-panel");
    const modal = !!state.inspection && readerRoom.matches;
    panel.setAttribute("role", modal ? "dialog" : "complementary");
    if (modal) panel.setAttribute("aria-modal", "true");
    else panel.removeAttribute("aria-modal");
    const background = [
      sidebar,
      ...requireElement("main").children,
      ...requireElement("views").children
    ];
    for (const element of background) {
      if (element instanceof HTMLElement && element.id !== "views" && element !== panel) {
        element.inert = modal || (element.classList.contains("view-container") && !element.classList.contains("active"));
      }
    }
    if (modal && !panel.contains(document.activeElement)) requireElement("detail-close").focus({ preventScroll: true });
  }

  /** Evidence keeps the selected subject and pins; its discovery survives Close and its target stays outside the reader. */
  function openInspection(target: InspectionTarget): void {
    const validated = scrubSnapshot({ ...readUrlState(), inspection: target }, nodesById, bundledMarkdown, directory => historyControl.hasDirectory(directory)).inspection;
    if (!validated) return;
    historyControl.pause();
    state.inspection = validated;
    if (state.view === "map" && state.perspective === "3d" && validated.kind !== "bundled-doc") {
      state.discovery = validated.kind === "file" ? { kind: "file", nodeId: validated.nodeId } : validated;
    }
    itemMenu.close();
    renderInspection();
    if (state.view === "map" && state.perspective === "3d") {
      const stepPanel = document.querySelector<HTMLElement>(".history-step-panel:not([hidden])");
      const room = requireElement("graph-svg").clientWidth - requireElement("detail-panel").offsetWidth - (stepPanel?.offsetWidth ?? 0);
      if (stepPanel && !readerRoom.matches && room < 320) historyControl.hideDetails();
      forceGraphView.render();
      if (validated.kind === "directory") forceGraphView.fit();
      else forceGraphView.keepInspectionVisible();
    }
    persistExploration();
  }

  /** Hiding by Close or by Back gives keyboard focus back to the visible inspection opener. */
  function hideInspection(): void {
    const returnFocus = document.activeElement instanceof HTMLElement && requireElement("detail-panel").contains(document.activeElement);
    detailPanel.hide();
    syncReaderPresentation();
    if (returnFocus) {
      const usable = (element: HTMLElement | null | undefined): element is HTMLElement => !!element?.isConnected
        && element.getClientRects().length > 0 && !element.closest("[hidden], [inert]");
      // A render rebuilds the cards, so an opener can be gone; its successor carries the same accessible name.
      const label = inspectionOpener?.getAttribute("aria-label");
      const successor = label ? document.querySelector<HTMLElement>(`[aria-label="${CSS.escape(label)}"]`) : null;
      const trayToggle = inspectionOpener?.closest(".pin-tray") ? document.querySelector<HTMLElement>(".pin-tray-toggle") : null;
      // A sphere tap records no opener. The map itself takes focus then, never a text field that raises a phone's keyboard.
      const target = [inspectionOpener, successor, trayToggle].find(usable) ?? document.querySelector<HTMLElement>(".view-container.active");
      if (target) focusWithoutTabStop(target);
    }
    inspectionOpener = null;
  }

  /** Focus an element; one that is not a control takes focus without joining the tab order. */
  function focusWithoutTabStop(element: HTMLElement): void {
    if (!element.hasAttribute("tabindex") && element.tabIndex < 0) {
      element.tabIndex = -1;
      element.addEventListener("blur", () => element.removeAttribute("tabindex"), { once: true });
    }
    element.focus({ preventScroll: true });
  }

  function closeInspection(persist = true): void {
    const wasOpen = !!state.inspection;
    state.inspection = null;
    hideInspection();
    if (wasOpen && state.view === "map" && state.perspective === "3d") forceGraphView.render();
    if (persist && wasOpen) persistExploration();
  }

  function showNodeActions(node: ExplorerNodePayload, symbol: string | undefined, anchor: { x: number; y: number }, onClose?: () => void): void {
    itemMenu.show(anchor, [
      { label: symbol ? `Inspect ${symbol === INTERNAL_SYMBOL ? "Internals" : symbol}` : "Inspect " + node.name, run: () => focusSidebar(node, symbol) },
      ...actionsForFile(node, symbol)
    ], onClose);
  }

  function centreOnDirectory(directory: string): void {
    state.discovery = null;
    closeInspection(false);
    leavePath();
    const current = state.openDirectories ?? new Set<string>();
    state.openDirectories = addOpenedDirectory(current, directory);
    if (state.view !== "map" || state.selectedNode) changePerspective(state.perspective, null);
    else if (state.openDirectories !== current) renderCurrentView();
    (state.perspective === "2d" ? localView : forceGraphView).focusDirectory(directory);
    persistExploration();
  }

  function showDirectoryActions(directory: string, anchor: { x: number; y: number }, onClose?: () => void): void {
    const opened = state.openDirectories?.has(directory) ?? false;
    itemMenu.show(anchor, [
      { label: "Inspect directory", run: () => openInspection({ kind: "directory", directory }) },
      { label: opened ? "Close directory" : "Open directory", run: () => opened ? closeRetainedDirectory(directory) : openRetainedDirectory(directory) },
      { label: "Centre on directory", run: () => centreOnDirectory(directory) },
      {
        label: "Clear pins inside this directory",
        run: () => applyRetentionChange(clearRetainedDirectory(retentionState(), directory, retainedPath),
          !!state.selectedNode && under(shownMoment?.fileAt(state.selectedNode.id) ?? state.selectedNode.codeRelativePath, shownMoment?.directoryAt(directory) ?? directory))
      }
    ], onClose);
  }

  function highlightSelectedCards(): void {
    localView.highlightSelection();
  }

  function openLocalViewForNode(target?: ExplorerNodePayload): void {
    const node = target ?? state.selectedNode;
    if (!node) {
      return;
    }
    recentreOn(node);
  }

  /** All portable exploration state is projected from its application owner in one place. */
  function explorationSnapshot(): UrlStateSnapshot {
    return {
      view: state.view,
      perspective: state.perspective,
      spatialLayout: state.spatialLayout,
      selectedNodeId: state.selectedNode?.id ?? null,
      pinSet: state.pins ?? EMPTY_PIN_SET,
      openDirectories: state.openDirectories ?? new Set(),
      retentionOrder: state.retentionOrder,
      filters: state.filters,
      layout: layoutSettingsOf(state.tuning),
      destination: state.destination ?? null,
      reachFrom: state.reachFrom ?? null,
      commit: state.historyCommit ?? null,
      authors: state.showAuthors ?? false,
      inspection: state.inspection,
      discovery: state.discovery ?? null
    };
  }

  /** Keep independent branches and explicitly open evidence in the portable snapshot. */
  function persistExploration(leavingPath = false): void {
    if (leavingPath) leavePath();
    syncPerspectiveControls();
    syncInspectionActions();
    schedulePersistNav();
    writeUrlState(explorationSnapshot());
  }

  /**
   * Draws the view shown. Called for a change of state, the Local Map is told to render, which it does at once when it
   * is the view shown and otherwise owes until it is; called to show a view, the Local Map runs an owed render or keeps
   * its picture and resumes its search (the owner's choice, 2026-10-16).
   */
  function renderCurrentView(reason: "change" | "show" = "change"): void {
    // History and navigation can interrupt an animated perspective change.
    if (changingPerspective && (state.view !== "map" || state.perspective !== transitionDestination)) {
      perspectiveEpoch++;
      releasePerspectiveCover(); cancelPerspectiveAnimation();
      changingPerspective = false; pendingPerspective = null; transitionDestination = null;
      syncPerspectiveControls();
    }
    syncPerspectiveControls();
    syncTuningControls();
    forceGraphView.setActive(state.view === "map" && state.perspective === "3d");
    if (reason === "change") localView.render();
    if (state.view === "sources") {
      doRenderSourcesView();
      return;
    }
    if (state.view === "world") {
      worldMapView.render();
      return;
    }
    if (graphData.nodes.length === 0) {
      return;
    }
    if (state.perspective === "2d") localView.show();
    else forceGraphView.render();
    syncPerspectiveControls();
  }

  const downloadCtx = { files, bundledMarkdown };

  /** The files a chosen start of the panel's walk names: the file, or every file under the directory. */
  function startFilesOf(start: PathDestination): string[] {
    return start.kind === "file" ? [start.nodeId] : graphData.nodes.map(node => node.id).filter(id => under(id, start.directory));
  }

  function doRenderSourcesView(): void {
    const reachFrom = state.reachFrom ?? null;
    renderSourcesView({
      // Today's docs, whatever the 3D picture showed last: the references are the sidebar's total, read, not recounted.
      facts: sourcesFacts(bundle.graph, { references: graphData.stats.references, ...(reachFrom ? { from: startFilesOf(reachFrom) } : {}), sampleRoots: bundle.sampleRoots ?? [] }),
      perspective: state.perspective,
      search: searchIndex,
      reachFrom,
      // The chosen start is exploration state: it rides the share link and the panel is drawn again from it.
      onReachFrom: (start: PathDestination | null) => {
        state.reachFrom = start;
        persistExploration();
        doRenderSourcesView();
      },
      bundledDocs,
      history: bundle.history,
      // A file named on the panel goes to the detail panel, where its doc and its doors are; the panel itself stays as it is.
      onFocusNode: (nodeId: string) => {
        const node = nodesById.get(nodeId);
        if (node) focusSidebar(node);
      },
      onViewBundledDoc: (docPath: string) => {
        showBundledDocInDetailPanel(docPath);
      },
      onDownload: (bundleType: DownloadBundleType, format: DownloadFormat) => void downloadDocs(bundleType, format, downloadCtx),
      onDownloadFacts: downloadFactsJson
    });
  }

  function showBundledDocInDetailPanel(path: string): void {
    openInspection({ kind: "bundled-doc", path });
  }

  function syncFilterControls(): void {
    if (filterToggleTests) {
      filterToggleTests.checked = state.filters.showTests;
    }
    if (filterToggleAssets) {
      filterToggleAssets.checked = state.filters.showAssets;
    }
    if (filterToggleRelatedDocs) {
      filterToggleRelatedDocs.checked = state.filters.showRelatedDocs;
    }
    if (filterToggleCitingDocs) {
      filterToggleCitingDocs.checked = state.filters.showCitingDocs;
    }
  }
}
